import React, { useState, useEffect } from 'react';
import { UserProfile } from '../../../../types/types';
import { AppSyncUser } from '../../../../services/auth/types/user';
import { useAppDispatch } from '../../../../layouts/App/hooks/useAppSelector';
import { updateUserProfile } from '../../../../store/slices/authSlice';
import PhotoUpload from '../../../../components/shared/PhotoUpload/PhotoUpload';
import Toast from '../../../../components/shared/Toast/Toast';
import { getUserDataFromCache } from '../../../../services/auth/utils/tokenCache';
import { useUser } from '../../../../services/auth/hooks/useUser';
import { ThemeSelector, LanguageSelector, CurrencySelector, TravelStyleSelector } from './components';
import './Settings.css';

interface SettingsProps {
  user: UserProfile;
  appSyncUser: AppSyncUser | null;
  onLogout: () => Promise<void>;
}

const Settings: React.FC<SettingsProps> = ({ user, appSyncUser, onLogout }) => {
  const dispatch = useAppDispatch();
  const { updateUser } = useUser();
  const [selectedPicture, setSelectedPicture] = useState<string | null>(null); // Base64 string for new picture
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Form state - initialized from AppSync user data (camelCase to match schema)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    nickname: ''
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    firstName?: string;
    lastName?: string;
  }>({});

  // Initialize form data from AppSync user when available
  useEffect(() => {
    if (appSyncUser) {
      setFormData({
        firstName: appSyncUser.firstName || '',
        lastName: appSyncUser.lastName || '',
        birthDate: appSyncUser.birthDate || '',
        nickname: appSyncUser.nickname || ''
      });
    }
  }, [appSyncUser]);

  const handlePhotoChange = (photoUrl: string) => {
    // photoUrl is a base64 data URL (e.g., "data:image/jpeg;base64,...")
    // Store it in state for preview and to include in updateUser when Save is pressed
    setSelectedPicture(photoUrl);
    // Update Redux state with base64 for immediate preview
    dispatch(updateUserProfile({ avatar: photoUrl }));
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation error for this field
    if (field === 'firstName' && validationErrors.firstName) {
      setValidationErrors(prev => ({ ...prev, firstName: undefined }));
    } else if (field === 'lastName' && validationErrors.lastName) {
      setValidationErrors(prev => ({ ...prev, lastName: undefined }));
    }
    // Clear success/error messages when user starts typing
    setSaveError(null);
    setSaveSuccess(false);
  };

  const validateForm = (): boolean => {
    const errors: { firstName?: string; lastName?: string } = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveChanges = async () => {
    // Clear previous messages
    setSaveError(null);
    setSaveSuccess(false);

    // Validate required fields
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Prepare AppSync user profile update (all user data is stored in AppSync)
      // Use camelCase to match GraphQL schema
      const appSyncUpdateInput: {
        firstName?: string;
        lastName?: string;
        nickname?: string;
        birthDate?: string;
        picture?: string;
      } = {
        firstName: formData.firstName.trim() || undefined,
        lastName: formData.lastName.trim() || undefined,
      };

      // Include optional fields if they have values
      if (formData.nickname.trim()) {
        appSyncUpdateInput.nickname = formData.nickname.trim();
      }
      if (formData.birthDate.trim()) {
        appSyncUpdateInput.birthDate = formData.birthDate.trim();
      }

      // Include picture if user selected a new one
      if (selectedPicture) {
        appSyncUpdateInput.picture = selectedPicture;
      }

      // Remove undefined fields
      Object.keys(appSyncUpdateInput).forEach(key => {
        if (appSyncUpdateInput[key as keyof typeof appSyncUpdateInput] === undefined) {
          delete appSyncUpdateInput[key as keyof typeof appSyncUpdateInput];
        }
      });

      // Update AppSync user profile (all user data is stored in AppSync, not Cognito)
      const updatedAppSyncUser = await updateUser(appSyncUpdateInput);

      if (updatedAppSyncUser) {
        // Clear selected picture after successful update
        setSelectedPicture(null);
        
        // Update Redux state with the picture_url from backend if available
        if (updatedAppSyncUser.picture_url) {
          dispatch(updateUserProfile({ avatar: updatedAppSyncUser.picture_url }));
        }
      }

      setSaveSuccess(true);
      console.log('✅ User data updated successfully');
    } catch (error: any) {
      console.error('❌ Failed to update user data:', error);
      setSaveError(error.message || 'Failed to update user data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Get email from AppSync (email comes from Cognito but is also stored in AppSync for display)
  // Email cannot be changed from the UI
  const email = appSyncUser?.email || user?.email || '';
  
  // Determine the current avatar to display
  // Priority:
  // 1. selectedPicture (base64 preview of newly selected image)
  // 2. appSyncUser.picture_url (backend URL from updateUserProfile)
  // 3. user.avatar from Redux
  // 4. appSync avatarUrl (legacy)
  // 5. default emoji
  const currentAvatar = selectedPicture || 
    appSyncUser?.picture_url || 
    user?.avatar || 
    appSyncUser?.avatarUrl || 
    '😊';

  return (
    <div className="settings-content">
      <div className="settings-header">
        <h2>Settings</h2>
        <p>Manage your account preferences and privacy settings</p>
      </div>

      <div className="settings-sections">
        {/* Profile Photo */}
        <div className="settings-section">
          <h3>Profile Photo</h3>
          <div className="settings-group">
            <PhotoUpload
              currentAvatar={currentAvatar}
              onPhotoChange={handlePhotoChange}
              size="large"
            />
          </div>
        </div>

        {/* Account Settings */}
        <div className="settings-section">
          <h3>Account Settings</h3>
          <div className="settings-group">
            <div className="setting-item">
              <label>Email <span style={{ color: '#ef4444' }}>*</span></label>
              <input 
                type="email" 
                value={email} 
                disabled 
                className="setting-input disabled"
                required
              />
            </div>
            <div className="setting-item">
              <label>First Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input 
                type="text" 
                value={formData.firstName} 
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Enter your first name"
                className={`setting-input ${validationErrors.firstName ? 'error' : ''}`}
                required
              />
              {validationErrors.firstName && (
                <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                  {validationErrors.firstName}
                </span>
              )}
            </div>
            <div className="setting-item">
              <label>Last Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input 
                type="text" 
                value={formData.lastName} 
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Enter your last name"
                className={`setting-input ${validationErrors.lastName ? 'error' : ''}`}
                required
              />
              {validationErrors.lastName && (
                <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                  {validationErrors.lastName}
                </span>
              )}
            </div>
            <div className="setting-item">
              <label>Nickname</label>
              <input 
                type="text" 
                value={formData.nickname} 
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                placeholder="Enter your nickname"
                className="setting-input"
              />
            </div>
            <div className="setting-item">
              <label>Birthdate</label>
              <input 
                type="date" 
                value={formData.birthDate} 
                onChange={(e) => handleInputChange('birthDate', e.target.value)}
                placeholder="YYYY-MM-DD"
                className="setting-input"
              />
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="settings-section">
          <h3>Preferences</h3>
          <div className="settings-group">
            <div className="setting-item">
              <label>Notifications</label>
              <div className="setting-toggle">
                <input type="checkbox" id="notifications" defaultChecked />
                <label htmlFor="notifications" className="toggle-label">
                  <span className="toggle-slider"></span>
                </label>
                <span>Email notifications</span>
              </div>
            </div>
            <div className="setting-item">
              <ThemeSelector />
            </div>
            <div className="setting-item">
              <LanguageSelector />
            </div>
            <div className="setting-item">
              <CurrencySelector />
            </div>
          </div>
        </div>

        {/* Travel Preferences */}
        <div className="settings-section">
          <h3>Travel Preferences</h3>
          <div className="settings-group">
            <div className="setting-item">
              <TravelStyleSelector />
            </div>
            <div className="setting-item">
              <label>Travel Notifications</label>
              <div className="setting-toggle">
                <input type="checkbox" id="travelNotifications" defaultChecked />
                <label htmlFor="travelNotifications" className="toggle-label">
                  <span className="toggle-slider"></span>
                </label>
                <span>Get travel deals and recommendations</span>
              </div>
            </div>
            <div className="setting-item">
              <label>Share Travel Updates</label>
              <div className="setting-toggle">
                <input type="checkbox" id="shareUpdates" defaultChecked />
                <label htmlFor="shareUpdates" className="toggle-label">
                  <span className="toggle-slider"></span>
                </label>
                <span>Share your trips with friends</span>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy & Security */}
        <div className="settings-section">
          <h3>Privacy & Security</h3>
          <div className="settings-group">
            <div className="setting-item">
              <label>Profile Visibility</label>
              <div className="setting-toggle">
                <input type="checkbox" id="profileVisibility" defaultChecked />
                <label htmlFor="profileVisibility" className="toggle-label">
                  <span className="toggle-slider"></span>
                </label>
                <span>Public profile</span>
              </div>
            </div>
            <div className="setting-item">
              <label>Location Sharing</label>
              <div className="setting-toggle">
                <input type="checkbox" id="locationSharing" />
                <label htmlFor="locationSharing" className="toggle-label">
                  <span className="toggle-slider"></span>
                </label>
                <span>Share location in posts</span>
              </div>
            </div>
            <div className="setting-item">
              <button className="setting-button secondary">Change Password</button>
            </div>
            <div className="setting-item">
              <button className="setting-button secondary">Two-Factor Authentication</button>
            </div>
            <div className="setting-item">
              <button className="setting-button secondary">Download My Data</button>
            </div>
          </div>
        </div>

        {/* Account Actions */}
        <div className="settings-section danger-section">
          <h3>Account Actions</h3>
          <div className="settings-group">
            <div className="setting-item">
              <button 
                className="setting-button primary" 
                onClick={handleSaveChanges}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            <div className="setting-item">
              <button className="setting-button danger" onClick={onLogout}>
                🚪 Sign Out
              </button>
            </div>
            <div className="setting-item">
              <button className="setting-button danger-outline">Delete Account</button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toast
        message={saveError || 'Failed to update user data. Please try again.'}
        type="error"
        isVisible={!!saveError}
        onClose={() => setSaveError(null)}
        duration={5000}
      />
      <Toast
        title="Success"
        message="User data updated successfully!"
        type="info"
        isVisible={saveSuccess}
        onClose={() => setSaveSuccess(false)}
        duration={3000}
      />
    </div>
  );
};

export default Settings;
